"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.KeyMap = void 0;
const util_1 = require("./util");
const SequenceMap_1 = require("./SequenceMap");
class KeyMap {
    constructor() {
        this.pressMap = new Map();
        this.releaseMap = new Map();
        this.chordMap = new Map();
        this.sequenceMap = new SequenceMap_1.SequenceMap();
    }
    _set(map, binding) {
        const list = map.get(binding.sig) ?? [];
        list.push(binding);
        map.set(binding.sig, list);
    }
    _del(map, sig, action) {
        const list = map.get(sig);
        if (!list)
            return;
        const index = list.findIndex((b) => b.action === action);
        if (index !== -1) {
            list.splice(index, 1);
            if (list.length === 0)
                map.delete(sig);
        }
    }
    bind(definitions) {
        const pressDefs = [];
        const releaseDefs = [];
        const chordDefs = [];
        const seqDefs = [];
        for (const def of definitions) {
            if (Array.isArray(def)) {
                const [rawSig, action, options] = def;
                if ((0, util_1.isSequenceSig)(rawSig)) {
                    seqDefs.push({ ...options, sig: rawSig, action: action });
                }
                else {
                    const sig = (0, util_1.expandMod)((0, util_1.normalize)(rawSig));
                    if ((0, util_1.isChordSig)(sig))
                        chordDefs.push({ ...options, sig, action: action });
                    else {
                        const b = { ...options, sig: sig, action: action };
                        if (b.release)
                            releaseDefs.push(b);
                        else
                            pressDefs.push(b);
                    }
                }
            }
            else {
                const rawB = def;
                if ((0, util_1.isSequenceSig)(rawB.sig)) {
                    seqDefs.push(rawB);
                }
                else {
                    const b = { ...rawB, sig: (0, util_1.expandMod)((0, util_1.normalize)(rawB.sig)) };
                    if ((0, util_1.isChordSig)(b.sig))
                        chordDefs.push(b);
                    else if (b.release)
                        releaseDefs.push(b);
                    else
                        pressDefs.push(b);
                }
            }
        }
        for (const b of pressDefs)
            this._set(this.pressMap, b);
        for (const b of releaseDefs)
            this._set(this.releaseMap, b);
        for (const b of chordDefs)
            this.setChord(b.sig, b.action, b);
        for (const b of seqDefs)
            this.setSequence(b.sig, b.action, b);
        return () => {
            for (const b of pressDefs)
                this._del(this.pressMap, b.sig, b.action);
            for (const b of releaseDefs)
                this._del(this.releaseMap, b.sig, b.action);
            for (const b of chordDefs)
                this.delChord(b.sig, b.action);
            for (const b of seqDefs)
                this.delSequence(b.sig, b.action);
        };
    }
    setSequence(sig, action, options) {
        this.sequenceMap.set(sig, action, options);
    }
    delSequence(sig, action) {
        this.sequenceMap.del(sig, action);
    }
    setPress(sig, action) {
        this._set(this.pressMap, { sig: (0, util_1.expandMod)(sig), action });
    }
    delPress(sig, action) {
        this._del(this.pressMap, (0, util_1.expandMod)(sig), action);
    }
    matchPress(press) {
        const sig = press.sig();
        const codeSig = press.codeSig();
        const exact = this.pressMap.get(sig);
        const code = sig !== codeSig ? this.pressMap.get(codeSig) : undefined;
        const any = this.pressMap.get('');
        const fallback = exact || code ? undefined : this.pressMap.get('?');
        const combined = [...(exact ?? []), ...(code ?? []), ...(any ?? []), ...(fallback ?? [])];
        return combined.length ? combined : undefined;
    }
    setRelease(sig, action) {
        this._set(this.releaseMap, { sig: (0, util_1.expandMod)(sig), action });
    }
    delRelease(sig, action) {
        this._del(this.releaseMap, (0, util_1.expandMod)(sig), action);
    }
    matchRelease(release) {
        const sig = release.sig();
        const codeSig = release.codeSig();
        const exact = this.releaseMap.get(sig);
        const code = sig !== codeSig ? this.releaseMap.get(codeSig) : undefined;
        const any = this.releaseMap.get('');
        const fallback = exact || code ? undefined : this.releaseMap.get('?');
        const combined = [...(exact ?? []), ...(code ?? []), ...(any ?? []), ...(fallback ?? [])];
        return combined.length ? combined : undefined;
    }
    setChord(sig, action, options) {
        const expanded = (0, util_1.expandMod)(sig);
        const list = this.chordMap.get(expanded) ?? [];
        list.push({ ...options, sig: expanded, action });
        this.chordMap.set(expanded, list);
    }
    delChord(sig, action) {
        const expanded = (0, util_1.expandMod)(sig);
        const list = this.chordMap.get(expanded);
        if (!list)
            return;
        const index = list.findIndex((b) => b.action === action);
        if (index !== -1) {
            list.splice(index, 1);
            if (list.length === 0)
                this.chordMap.delete(sig);
        }
    }
    /**
     * Returns matching chord bindings for the current `pressed` set, or
     * `undefined` if there are fewer than 2 pressed keys or no match.
     */
    matchChord(pressed) {
        if (pressed.keys.length < 2)
            return undefined;
        const sig = pressed.sig();
        const matches = this.chordMap.get(sig);
        return matches?.length ? matches : undefined;
    }
}
exports.KeyMap = KeyMap;
